import { requestV3 } from 'Features/Auth/axios-client'
import {
  useMutation,
  useQuery,
  useQueryClient,
  UseQueryResult,
} from '@tanstack/react-query'
import {
  InventoryResult,
  InventorySummary,
} from 'Features/Projects/Scenarios/Inventory/Inventory.types'

// Safety-stock runs write 'processing' and then 'completed' or 'failed' to each
// site. Re-read while any site is still processing, then stop.
const SAFETY_STOCK_POLL_MS = 10 * 1000

const hasProcessingSite = (sites: InventorySummary[] = []) =>
  sites.some((site) =>
    ['processing', 'initiated', 'running'].includes(
      String(site.safetyStockStatus).toLowerCase()
    )
  )

// The worker only writes 'processing' once its job has started, which can be a
// minute or two after Calculate. Keep polling for that window after a submit,
// so the status change is picked up without a manual refresh.
const SUBMIT_WATCH_MS = 3 * 60 * 1000
const lastSubmitted = new Map<string, number>()
const scenarioKey = (projectId: string, scenarioId: string) =>
  `${projectId}:${scenarioId}`
const recentlySubmitted = (projectId: string, scenarioId: string) =>
  Date.now() - (lastSubmitted.get(scenarioKey(projectId, scenarioId)) ?? 0) <
  SUBMIT_WATCH_MS

const fetchInventorySummary = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/inventory-site-summary?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useInventorySummary = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<InventorySummary[]> => {
  return useQuery({
    queryKey: ['inventory-site-summary', projectId, scenarioId],
    queryFn: () => fetchInventorySummary(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
    refetchInterval: (query) =>
      hasProcessingSite(
        (query.state.data as { data?: InventorySummary[] } | undefined)?.data
      ) || recentlySubmitted(projectId, scenarioId)
        ? SAFETY_STOCK_POLL_MS
        : false,
  })
}

const fetchInventoryResults = async (projectId: string, scenarioId: string) => {
  const { data } = await requestV3({
    url: `/inventory-site-results?projectId=${projectId}&scenarioId=${scenarioId}`,
  })
  return data
}

export const useInventoryResults = ({
  projectId,
  scenarioId,
}: {
  projectId: string
  scenarioId: string
}): UseQueryResult<InventoryResult[]> => {
  return useQuery({
    queryKey: ['inventory-site-results', projectId, scenarioId],
    queryFn: () => fetchInventoryResults(projectId, scenarioId),
    select: (data) => data.data,
    gcTime: Infinity,
  })
}

export type InventoryPayloadType = {
  siteKey: number
}

const calculateSafetyStock = async ({
  projectId,
  scenarioId,
  payload,
}: {
  projectId: string
  scenarioId: string
  payload: InventoryPayloadType[]
}) => {
  const { data } = await requestV3({
    url: `/inventory/safety-stock/calculate?projectId=${projectId}&scenarioId=${scenarioId}`,
    method: 'PUT',
    data: payload,
  })
  return data
}

export const useCalculateSafetyStock = (
  onSuccess: () => void,
  onError: () => void
) => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: calculateSafetyStock,
    onSuccess: async (_, { projectId, scenarioId }) => {
      lastSubmitted.set(scenarioKey(projectId, scenarioId), Date.now())
      // This used to invalidate ['replenishment-site-summary'], a key no query
      // uses, so the site list never refreshed after a calculation.
      await queryClient.invalidateQueries({
        queryKey: ['inventory-site-summary', projectId, scenarioId],
      })
      onSuccess()
    },
    onError,
  })
}
